import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollToId } from "../../lib/lenis";

const HERO_IMG = "/images/gen/hero-art.jpg";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-screen flex flex-col justify-between overflow-hidden bg-bone pt-[72px]">
      <div className="max-w-[1600px] w-full mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-10 lg:gap-6 flex-1 items-center pt-14 lg:pt-0">
        <div className="lg:col-span-7 relative z-10">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.35em] text-beiged mb-8"
            data-testid="hero-eyebrow"
          >
            European craft · Indian soul — Since 2009, Mumbai
          </motion.p>

          <h1 className="font-serif tracking-tight leading-[1.04] text-ink text-[13.5vw] sm:text-[11vw] lg:text-[6.6vw]" data-testid="hero-title">
            <Line delay={0.35}>The Innovation</Line>
            <Line delay={0.47}><em className="italic text-beiged">product portfolio</em></Line>
            <Line delay={0.59}>2026 edition.</Line>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95 }}
            className="mt-8 max-w-md text-sm sm:text-base leading-relaxed text-muted2"
          >
            A portfolio in eight chapters — PU, Solidwood, Membrane, Velora European Laminate,
            Ceramic, Acrylic, Toughened Glass and made-to-measure Wardrobe Shutters —
            photographed sample by sample, with every shade palette laid beneath each piece.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-explore-btn"
              onClick={() => scrollToId("pu")}
              className="group flex items-center gap-3 bg-ink text-bone rounded-full pl-7 pr-3 py-3 text-xs font-mono uppercase tracking-[0.25em] hover:bg-ink2 transition-colors"
            >
              Explore the collection
              <span className="w-8 h-8 rounded-full bg-bone/15 flex items-center justify-center transition-transform duration-500 group-hover:translate-y-0.5">
                <ArrowDown size={13} strokeWidth={1.5} />
              </span>
            </button>
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted2">8 finish chapters · shade palettes</span>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.4, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
            className="relative aspect-[3/4] max-h-[78vh] w-full overflow-hidden"
            data-testid="hero-image-frame"
          >
            <motion.img
              src={HERO_IMG}
              alt="Exploded-view modular cabinet artwork — Innovation Modular System 2026"
              style={{ y }}
              className="absolute inset-0 w-full h-[120%] object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 ring-1 ring-inset ring-ink/10" />
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.4 }}
        className="max-w-[1600px] w-full mx-auto px-5 sm:px-8 lg:px-12 pb-10"
      >
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 border-t border-ink/10 pt-8" data-testid="hero-stats">
          {[
            ["12,000", "projects every year"],
            ["17", "years of experience"],
            ["2,50,000", "sq ft manufacturing unit"],
            ["2009", "founded in Mumbai"]
          ].map(([v, l]) => (
            <div key={l}>
              <p className="font-serif text-2xl sm:text-3xl text-ink">{v}</p>
              <p className="font-mono text-[9px] sm:text-[10px] uppercase tracking-[0.25em] text-muted2 mt-2">{l}</p>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}
